import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CalendarDays, ImageUp, RotateCcw, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useEvent } from '../i18n/EventProvider';
import { updateEvent, uploadFlyer, resetFlyer } from '../lib/content';

const FIELDS = [
  { k: 'name', label: 'Event name (English)', ph: 'Coffee & Conversation' },
  { k: 'name_es', label: 'Event name (Spanish)', ph: 'Café y Conversación' },
  { k: 'date', label: 'Date', type: 'date' },
  { k: 'time', label: 'Start time', type: 'time' },
  { k: 'venue', label: 'Venue', ph: 'Schuster Student Success Center', span: 2 },
  { k: 'venue_short', label: 'Short venue name', ph: 'Schuster SSC' },
  { k: 'room', label: 'Room number', ph: '130' },
  { k: 'campus', label: 'Campus / building', ph: 'Columbus State University' },
  { k: 'address', label: 'Street address', ph: '4107 University Ave, Columbus, GA 31907' },
];

const toForm = (e) => ({ name: e.name, name_es: e.name_es || '', date: e.date_iso, time: e.time_24, venue: e.venue_name, venue_short: e.venue_short, room: e.room, campus: e.campus, address: e.address });
const inputCls = 'mt-2 bg-[#faf6ea] border-[#e5dcc5] h-11 rounded-xl focus-visible:ring-[#2e5340]';
const errMsg = (err) => { const d = err?.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(', ') : d || 'Please try again.'; };

export default function EventAdmin() {
  const { toast } = useToast();
  const { event, setEvent, flyerUrl } = useEvent();
  const [form, setForm] = useState(toForm(event));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setForm(toForm(event)); }, [event]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await updateEvent(form);
      setEvent(r.event);
      toast({ title: 'Gathering updated', description: 'The whole site now shows the new details.' });
    } catch (err) {
      toast({ title: 'Save failed', description: errMsg(err) });
    } finally { setSaving(false); }
  };

  const onFlyer = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadFlyer(file);
      setEvent(r.event);
      toast({ title: 'Flyer updated', description: 'The new flyer is live on the site.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: errMsg(err) });
    } finally { setUploading(false); }
  };

  const onReset = async () => {
    if (!window.confirm('Go back to the built-in flyer?')) return;
    try { const r = await resetFlyer(); setEvent(r.event); } catch (err) { toast({ title: 'Reset failed', description: errMsg(err) }); }
  };

  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-10" data-testid="admin-event-section">
      <h2 className="font-serif-display text-2xl text-[#1f3a2a] inline-flex items-center gap-2"><CalendarDays size={22} className="text-[#b88a2a]" /> Next gathering</h2>
      <p className="text-sm text-[#4a5a4e] mt-1">Changes update the top of the site, RSVP, footer, calendar invites, reminder emails, and the Spanish version.</p>
      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <form onSubmit={save} className="lg:col-span-3 bg-white border border-[#e5dcc5] rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.k} className={f.span === 2 ? 'sm:col-span-2' : ''}>
              <Label className="text-[#1f3a2a]">{f.label}</Label>
              <Input data-testid={`admin-event-${f.k}-input`} type={f.type || 'text'} value={form[f.k] || ''} placeholder={f.ph} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className={inputCls} />
            </div>
          ))}
          <Button type="submit" disabled={saving} data-testid="admin-event-save-btn" className="sm:col-span-2 bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-11 font-medium">
            <Save size={16} className="mr-2" /> {saving ? 'Saving…' : 'Save gathering'}
          </Button>
        </form>
        <div className="lg:col-span-2 bg-white border border-[#e5dcc5] rounded-2xl p-6">
          <Label className="text-[#1f3a2a]">Flyer image</Label>
          <img src={flyerUrl} alt="Current flyer" data-testid="admin-flyer-preview" className="mt-3 w-full max-h-80 object-contain rounded-xl bg-[#faf6ea]" />
          <label className="mt-4 flex items-center justify-center gap-2 cursor-pointer rounded-full h-11 bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3] text-sm font-medium transition-colors">
            <ImageUp size={16} /> {uploading ? 'Uploading…' : 'Upload new flyer'}
            <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="admin-flyer-input" className="hidden" disabled={uploading} onChange={(e) => { onFlyer(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <p className="mt-2 text-xs text-[#8a7a4e]">PNG, JPG or WEBP up to 10 MB.</p>
          {event.flyer_url && (
            <button type="button" onClick={onReset} data-testid="admin-flyer-reset-btn" className="mt-3 inline-flex items-center gap-1 text-xs text-[#8a7a4e] hover:text-[#1f3a2a]"><RotateCcw size={12} /> Use built-in flyer</button>
          )}
        </div>
      </div>
    </section>
  );
}
